import type { JSX } from "react"
import * as React from "react"
import { useEffect, useRef, useState } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { cn } from "@ui/utils"
import { $getRoot } from "lexical"

import { shouldShowPlaceholderFromRoot } from "./placeholder-visibility"
import styles from "./styles.module.css"

type Props = {
  placeholder: string
  proseClasses?: string
  contentStyle?: React.CSSProperties
  onKeyDown?: (event: React.KeyboardEvent) => void
}

export default function LexicalContentEditable({
  placeholder,
  proseClasses = "prose-lexical dark:prose-invert",
  contentStyle,
  onKeyDown,
}: Props): JSX.Element {
  const contentEditableRef = useRef<HTMLDivElement>(null)
  const [editor] = useLexicalComposerContext()
  const [showPlaceholder, setShowPlaceholder] = useState(true)

  useEffect(() => {
    const checkIfEmpty = () => {
      editor.getEditorState().read(() => {
        const root = $getRoot()
        setShowPlaceholder(shouldShowPlaceholderFromRoot(root))
      })
    }

    // Initial check
    checkIfEmpty()

    // Listen for updates
    const unregister = editor.registerUpdateListener(() => {
      checkIfEmpty()
    })

    return unregister
  }, [editor])

  return (
    <div
      className={cn(styles.variables, "intulse-disable-click-to-call relative min-h-full w-full", proseClasses)}
      style={contentStyle}
    >
      <ContentEditable
        ref={contentEditableRef}
        className={`${styles.root} relative block w-full border-0 outline-0`}
        tabIndex={0}
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        onKeyDown={onKeyDown}
      />
      {showPlaceholder && (
        <div
          className={`${styles.placeholder} text-muted-foreground pointer-events-none absolute top-[var(--lexical-editor-placeholder-top,2px)] right-7 left-[var(--lexical-editor-placeholder-left,2px)] inline-block cursor-text overflow-hidden text-ellipsis whitespace-nowrap`}
        >
          {placeholder}
        </div>
      )}
    </div>
  )
}
